import { useCallback, useMemo, useState } from 'react';
import type { IColumnConfig } from '@/@types/tableColumnVisibility';
import type { ITableSortState, ITableSortConfig } from '@/@types/tableSort';

/**
 * Generic table sorting hook.
 * Manages sort state (column + direction) and returns sorted items.
 */
export function useTableSort<T>(
  items: T[],
  columns: IColumnConfig[],
  config: ITableSortConfig,
  accessor: (item: T, columnId: string) => string | number
): {
  sortState: ITableSortState;
  toggleSort: (columnId: string) => void;
  sortedItems: T[];
} {
  const [sortState, setSortState] = useState<ITableSortState>(config.defaultSort);

  const columnMap = useMemo(() => {
    const map = new Map<string, IColumnConfig>();
    for (const col of columns) {
      map.set(col.id, col);
    }
    return map;
  }, [columns]);

  const toggleSort = useCallback(
    (columnId: string) => {
      const col = columnMap.get(columnId);
      if (!col?.sortType) return;

      setSortState(prev => {
        if (prev.sortBy === columnId) {
          return {
            sortBy: columnId,
            direction: prev.direction === 'asc' ? 'desc' : 'asc',
          };
        }
        return { sortBy: columnId, direction: 'asc' };
      });
    },
    [columnMap]
  );

  const sortedItems = useMemo(() => {
    const col = columnMap.get(sortState.sortBy);
    if (!col?.sortType) return items;

    const sorted = [...items];
    const { sortType, customSortOrder } = col;
    const multiplier = sortState.direction === 'asc' ? 1 : -1;

    sorted.sort((a, b) => {
      const aVal = accessor(a, sortState.sortBy);
      const bVal = accessor(b, sortState.sortBy);

      let comparison = 0;

      switch (sortType) {
        case 'string':
          comparison = String(aVal).localeCompare(String(bVal));
          break;
        case 'number':
        case 'date':
          comparison = Number(aVal) - Number(bVal);
          break;
        case 'custom': {
          if (!customSortOrder) break;
          const aIndex = customSortOrder.indexOf(String(aVal));
          const bIndex = customSortOrder.indexOf(String(bVal));
          comparison =
            (aIndex === -1 ? customSortOrder.length : aIndex) -
            (bIndex === -1 ? customSortOrder.length : bIndex);
          break;
        }
      }

      return comparison * multiplier;
    });

    return sorted;
  }, [items, sortState.sortBy, sortState.direction, columnMap, accessor]);

  return { sortState, toggleSort, sortedItems };
}
